import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FilePlus, Save, Printer, Handshake, DoorClosed, FileSignature } from "lucide-react";
import { openReport, buildReportName } from "../lib/reportViewerBus";
import AvocatAutocomplete from "./AvocatAutocomplete";
import DateInput from "./DateInput";
import { WinButton } from "./WinComponents";
import EntenteModal from "./EntenteModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal Attestation — parité stricte VB.NET `frmAttest` (v10 : layout captures réelles).
 * -----------------------------------------------------------------------------
 * Structure exacte du legacy (validée sur captures 2026-02-16) :
 *   ┌── Toolbar : [Nouvelle] [Enregistrer] [Imprimer] [Entente] [Fermer]
 *   ┌── Header  : No. Dossier · No. Référence · Région · Bureau · Date émise
 *   ┌── 3 tabs  : [Demande d'aide juridique] [Attestation] [Liste]
 *
 *   Tab 1 (Demande) :
 *     • Bandeau "Requérant" (teal) : Nom | Prénom | Date naiss. / Adresse
 *     • 2 grilles teal : Demandes disponibles ↔ Demandes sélectionnées
 *       + boutons transfert -> >> <- <<
 *     • Bandeau "Volet contributif" (teal) : ContrMax + CoutPrev
 *       + Entente Oui/Non (frame séparé)
 *
 *   Tab 2 (Attestation) :
 *     • Bandeau "Nature et objet du dossier" (teal) — textarea multi-lignes
 *     • 2 colonnes :
 *         [Dossier confié à] : Code | Nom | Adresse (3 lignes)
 *         [Attestation]      : Type | Durée du | Jusqu'au | Code avocat signataire
 *     • Bas : [Ajouter fondement de droit] [Ajouter les notes]
 *
 *   Tab 3 (Liste) :
 *     • Grille 7 colonnes (ordre exact) :
 *         No. référence | Image | Volet contrib. | Date émis | Nom avocat | No. dossier | No. séquence
 *     • Boutons droite verticaux : [Amender] [Ouvrir] [Supprimer]
 */

// Palette alignée sur le look & feel de l'application (App.css --primary-blue)
const TEAL_HEADER = "#0033A0";
const TEAL_BG_LIGHT = "#f0f4f8";

const EMPTY_ATTEST = {
  adattypedossier: "R",
  adattype: "SRG",
  amende_label: "Nouvelle attestation",  // cboTypeAttest.Text (affichage humain)
  adatdateemis: new Date().toISOString().slice(0, 10),
  adatdateacceptdeb: new Date().toISOString().slice(0, 10),
  adatdateacceptfin_date: "",        // vide = Jusqu'à terme
  codecateg: "", adatnature: "",
  adatdemdef: 2, adatregcond: 1,
  adatcodeavomand: "", adatavonommand: "", adatavoprenommand: "",
  adatavoadr: "", adatavoville: "", adatavoprov: "QC", adatavocodepostal: "",
  adatadmfin: 0, adatmntcontrib: "0", adatentente: 0,
  adatcodeavocsign: "", adatavonomsign: "", adatavoprenomsign: "",
  notes: "", fondement_droit: "",
  coureelpre: 0, versement: 0,
};

const GRID_COLS_DISPO = [
  { key: "noseqdaj", label: "No.\u00A0séq.", width: 100 },
  { key: "no_doss_cour", label: "No. Doss.Cour", width: 100 },
  { key: "nature_code", label: "Nature", width: 70 },
  { key: "avocat_code", label: "Avocat", width: 70 },
  { key: "dem_def", label: "Dem/Def", width: 80 },
  { key: "decision", label: "Décision", width: 80 },
];

// Ordre EXACT observé sur capture legacy — INVERSE des colonnes Disponibles
const GRID_COLS_LISTE = [
  { key: "noref", label: "No. référence", width: 110 },
  { key: "image", label: "Image", width: 60 },
  { key: "volet_contrib", label: "Volet contrib.", width: 80 },
  { key: "date_emis", label: "Date émis", width: 90 },
  { key: "nom_avocat", label: "Nom avocat", width: 130 },
  { key: "no_dossier", label: "No. dossier", width: 90 },
  { key: "no_sequence", label: "No. séquence", width: 100 },
];

// Section header teal (parité captures)
const SectionHeader = ({ title }) => (
  <div style={{
    background: TEAL_HEADER, color: "#fff", padding: "3px 8px",
    fontSize: 12.5, fontWeight: 600, textAlign: "center",
  }}>{title}</div>
);

// Bouton toolbar legacy remplacé par WinButton — voir barre outils ci-dessous

const AttestationModal = ({ isOpen, onClose, noclient, pkDaj, showPopup,
                           requerantNom, requerantPrenom, formData, onAttestationCreated,
                           isAdmin = false, canSupprimer = false }) => {
  const [activeTab, setActiveTab] = useState("demande");
  const [form, setForm] = useState({ ...EMPTY_ATTEST });
  const [avocats, setAvocats] = useState([]);
  const [header, setHeader] = useState(null);
  const [dispoRows, setDispoRows] = useState([]);
  const [selecRows, setSelecRows] = useState([]);
  const [dispoSel, setDispoSel] = useState([]); // multi-sélection (indices)
  // Limite de caractères du champ « Nature et objet du dossier » (adatnature).
  // Avec plusieurs demandes, le texte cumulé peut devenir long → on valide/avertit.
  const MAX_NATURE_LEN = 2000;
  const [selecSelIdx, setSelecSelIdx] = useState(-1);
  const [attestations, setAttestations] = useState([]);
  const [listeSelIdx, setListeSelIdx] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [substDialog, setSubstDialog] = useState(null); // { noref, regbur }
  const [hasSubstitution, setHasSubstitution] = useState(false); // attest. ouverte/sélec. a une subst. de procureur
  const [dateEmiseError, setDateEmiseError] = useState("");
  // Mode amendement (parité `ReqAmendeAttest`) : quand actif, l'enregistrement
  // crée une nouvelle image (-02) au lieu d'une nouvelle attestation.
  const [amendMode, setAmendMode] = useState(false);
  const [amendSourceNoref, setAmendSourceNoref] = useState("");
  const [amendType, setAmendType] = useState("");
  const [enteneModal, setEnteneModal] = useState(null);   // {noref, regbur} | null
  const AMEND_TYPES = [
    "Ajout/Retrait de dossier",
    "Modification de nature",
    "Substitution de procureur",
    "Changement volet contributif",
  ];

  // Flag pour bypass le useEffect auto-compute nature/avocats quand on ouvre
  // une attestation existante depuis la grille Liste (parité `OuvreAttest`).
  // Sans ce flag, `setSelecRows(dajsLies)` déclenche le useEffect qui écrase
  // tous les champs `adat*` chargés depuis la DB avec des valeurs recomputées.
  const skipSelecRowsEffectRef = useRef(false);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ────────────────────────────────────────────────────────────────────────
  // Validation Date émise (parité legacy MDIParent2.vb : `Enr.ADATDateEmis`
  // ne peut pas être antérieure à la date de demande ni postérieure à today)
  // Retourne "" si OK, sinon message d'erreur français à afficher.
  //
  // ⚠ Bug fuseau horaire (v14→v15) : `new Date("YYYY-MM-DD")` est parsé en UTC
  // puis converti au fuseau local, ce qui décale la date d'un jour dans les
  // fuseaux occidentaux (ex: EDT UTC-4 → 2026-07-11 UTC devient 2026-07-10
  // local, faussement égal à today). On parse donc explicitement en local.
  // ────────────────────────────────────────────────────────────────────────
  const parseLocalDate = (s) => {
    if (!s || typeof s !== "string" || s.length < 10) return null;
    const [y, m, d] = s.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);  // Local midnight — pas de conversion timezone
  };

  const validateDateEmise = useCallback((dateStr, dateDemande) => {
    if (!dateStr || dateStr.length < 10) return "";
    const parsed = parseLocalDate(dateStr);
    if (!parsed || isNaN(parsed.getTime())) return "Date invalide.";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (parsed > today) {
      return `La date d'émission ne peut pas être postérieure à aujourd'hui (${today.toISOString().slice(0, 10)}).`;
    }
    if (dateDemande) {
      const dd = parseLocalDate(dateDemande);
      if (dd && !isNaN(dd.getTime()) && parsed < dd) {
        return `La date d'émission (${dateStr}) ne peut pas être antérieure à la date de la demande (${dateDemande}).`;
      }
    }
    return "";
  }, []);

  // Date de la demande la plus ancienne parmi les DAJ sélectionnés (limite basse)
  const dateDemandeMin = selecRows.length > 0
    ? selecRows
        .map(r => r.date_demande || "")
        .filter(Boolean)
        .sort()[0] || ""
    : "";

  // Re-valide dès qu'un DAJ est transféré ou que la date change
  useEffect(() => {
    setDateEmiseError(validateDateEmise(form.adatdateemis, dateDemandeMin));
  }, [form.adatdateemis, dateDemandeMin, validateDateEmise]);

  const loadEligibles = useCallback(async () => {
    if (!noclient) return;
    try {
      const { data } = await axios.get(`${API}/clients/${noclient}/daj-eligibles-attestation`);
      setDispoRows(data || []);
      setSelecRows([]);
      setDispoSel([]); setSelecSelIdx(-1);
    } catch { setDispoRows([]); }
  }, [noclient]);

  const loadAttestations = useCallback(async () => {
    if (!noclient) return;
    try {
      const { data } = await axios.get(`${API}/clients/${noclient}/attestations`);
      // Mappe les données API vers colonnes grille Liste legacy
      setAttestations((data || []).map((a, i) => ({
        ...a,
        noref: a.noref || "",
        image: a.noref ? a.noref.split("-").pop() : "01",
        volet_contrib: (parseFloat(a.adatmntcontrib || 0) > 0) ? "Oui" : "Non",
        date_emis: (a.adatdateemis || "").substring(0, 10),
        nom_avocat: [a.adatavoprenomsign, a.adatavonomsign].filter(Boolean).join(" ") || "-",
        no_dossier: a.nodossier || "-",
        no_sequence: a.rowId || i,
      })));
    } catch { setAttestations([]); }
  }, [noclient]);

  const loadHeader = useCallback(async () => {
    if (!noclient) return;
    try {
      const { data } = await axios.get(`${API}/clients/${noclient}/info-attestation-header`);
      setHeader(data);
    } catch { setHeader(null); }
  }, [noclient]);

  // Reset du form uniquement lors de l'ouverture initiale de la modale ou
  // changement de client. ⚠ NE PAS mettre `formData` dans les deps sinon
  // le form se remet à zéro à chaque mutation du parent App.js —
  // écrasant les données peuplées par `handleOuvrirAttestExistante`.
  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${API}/reference/avocats`).then(r => setAvocats(r.data || []));
    loadHeader();
    loadEligibles();
    loadAttestations();
    setForm({ ...EMPTY_ATTEST,
      adatreqnom: requerantNom || "",
      adatreqprenom: requerantPrenom || "",
      coureelpre: formData?.service?.cout_reel || 0,
    });
    setActiveTab("demande");
  }, [isOpen, noclient]);

  // Charge une attestation existante par son `noref` : peuple tous les champs
  // `adat*`, remplit la grille "Demandes sélectionnées" et bascule sur l'onglet
  // Attestation. `silent` = pas de popup (utilisé pour l'auto-ouverture).
  // Parité legacy `OuvreAttest` (CodeAttest.vb:1958).
  const loadAttestation = useCallback(async (noref, silent = false) => {
    if (!noref) return;
    try {
      const { data } = await axios.get(`${API}/attestations/${encodeURIComponent(noref)}`);
      const att = data.attestation || {};
      const dajsLies = data.dajs_lies || [];
      const attTrimmed = Object.fromEntries(
        Object.entries(att).map(([k, v]) => [k, typeof v === "string" ? v.trimEnd() : v])
      );
      setForm(prev => ({ ...prev, ...attTrimmed,
        noref,
        adatdateemis: (attTrimmed.adatdateemis || prev.adatdateemis || "").substring(0, 10),
        adatdateacceptdeb: (attTrimmed.adatdateacceptdeb || "").substring(0, 10),
        adatdateacceptfin_date: (attTrimmed.adatdateacceptfin || "").split("|")[1] || "",
      }));
      skipSelecRowsEffectRef.current = true;
      setSelecRows(dajsLies.map(d => ({
        noseqdaj: d.noseqdaj || "",
        no_doss_cour: d.nodossier || "",
        nature_code: d.adadcodenature || "",
        avocat_code: d.adda712 || "",
        avocat_signataire_code: d.adda704 || "",
        dem_def: d.adda540 === 1 ? "En demande" : "En défense",
        decision: "Admis.",
        date_demande: d.date_demande || "",
      })));
      setDispoRows([]);
      setActiveTab("attestation");
      if (!silent) {
        showPopup?.(`Attestation ${noref} ouverte pour consultation.`,
                    att.adatamendable === "A" ? "info" : "warning", "Attestation");
      }
    } catch (e) {
      if (!silent) {
        showPopup?.(`Erreur lors du chargement de l'attestation : ${e.response?.data?.detail || e.message}`,
                    "error", "Attestation");
      } else {
        console.error("Auto-ouverture attestation échouée:", e);
      }
    }
  }, [showPopup]);

  // À l'ouverture, une fois `attestations` chargé :
  //   - Si une attestation correspond au No. Référence de la demande courante
  //     → on l'ouvre automatiquement (onglet Attestation peuplé).
  //   - Sinon, s'il existe des attestations → onglet Liste ; sinon → Demande.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { autoOpenedRef.current = false; return; }
    if (!attestations || attestations.length === 0) { setActiveTab("demande"); return; }
    if (autoOpenedRef.current) return;
    const curRef = (formData?.no_reference || "").trim();
    const match = curRef
      ? attestations.find(a => (a.noref || "").trim() === curRef)
      : null;
    autoOpenedRef.current = true;
    if (match) {
      loadAttestation(match.noref, true);   // silencieux
    } else {
      setActiveTab("liste");
    }
  }, [isOpen, attestations, formData?.no_reference, loadAttestation]);

  // Détection substitution de procureur pour l'attestation RÉELLEMENT CHARGÉE
  // (celle affichée dans « No. Référence » = form.noref, peuplée par un double-clic
  // ou le bouton Ouvrir). On NE se base PAS sur la simple ligne surlignée dans la
  // grille : sélectionner une ligne sans charger ses données ne doit pas activer le
  // bouton. Parité legacy ValideSiSubstProc — le bouton n'est actif QUE lorsque
  // l'endpoint substitution-check répond true pour l'attestation ouverte.
  const substNoref = (form.noref || "").trim();
  const substRegbur =
    ((form.noreg || formData?.region || "").padStart(2, "0").slice(0, 2))
    + ((form.nobur || formData?.bureau || "").padStart(2, "0").slice(0, 2));
  useEffect(() => {
    if (!substNoref || substRegbur.length !== 4) { setHasSubstitution(false); return; }
    let cancelled = false;
    axios.get(`${API}/attestation/${encodeURIComponent(substNoref)}/substitution-check`,
      { params: { regbur: substRegbur } })
      .then((r) => { if (!cancelled) setHasSubstitution(!!r.data?.substitution); })
      .catch(() => { if (!cancelled) setHasSubstitution(false); });
    return () => { cancelled = true; };
  }, [substNoref, substRegbur]);

  const openSubstitution = useCallback(() => {
    if (!substNoref || substRegbur.length !== 4) {
      return showPopup?.("Ouvrez ou sélectionnez d'abord une attestation avec substitution de procureur.",
        "warning", "Substitution de procureur");
    }
    setSubstDialog({ noref: substNoref, regbur: substRegbur });
  }, [substNoref, substRegbur, showPopup]);

  // === Entente de paiement (parité BtnEntente / RadYes / RadNo) ===
  const openEntente = useCallback(() => {
    const row = attestations[listeSelIdx];
    const nrf = row?.noref || form.noref;
    if (!nrf)
      return showPopup?.("Ouvrez ou sélectionnez d'abord une attestation.", "warning", "Entente");
    const rr = (row?.noreg || form.noreg || "").padStart(2, "0").slice(0, 2);
    const bb = (row?.nobur || form.nobur || "").padStart(2, "0").slice(0, 2);
    setEnteneModal({ noref: nrf, regbur: rr + bb });
  }, [attestations, listeSelIdx, form.noref, form.noreg, form.nobur, showPopup]);

  const handleEntenteNon = useCallback(async () => {
    setField("adatentente", 0);
    const nrf = attestations[listeSelIdx]?.noref || form.noref;
    if (!nrf) return;
    if (!window.confirm("Supprimer l'entente de paiement de cette attestation ?")) return;
    try {
      await axios.delete(`${API}/attestations/${encodeURIComponent(nrf)}/entente`);
      showPopup?.("Entente supprimée.", "info", "Entente");
    } catch (e) {
      if (e.response?.status !== 404)
        showPopup?.(e.response?.data?.detail || "Erreur suppression entente.", "error", "Entente");
    }
  }, [attestations, listeSelIdx, form.noref, showPopup]);  // eslint-disable-line react-hooks/exhaustive-deps

  // === Transferts ===
  const transferOne = (from, to, idx, setFrom, setTo, setSel) => {
    if (idx < 0 || idx >= from.length) return;
    setTo([...to, from[idx]]);
    setFrom(from.filter((_, i) => i !== idx));
    setSel(-1);
  };
  const transferAll = (from, to, setFrom, setTo, setSel) => {
    setTo([...to, ...from]); setFrom([]); setSel(-1);
  };
  // Règle métier : une attestation ne regroupe que des demandes du MÊME avocat
  // confié (adda712). EXCEPTION Legacy : « Substitution de procureur » — ce type
  // d'amendement change justement le procureur, donc on autorise un avocat confié
  // différent (cf. frmAttest.NouvReqAjout + ValideSiSubstProc). Le blocage reste
  // actif pour les 3 autres types et pour la création d'attestation.
  const _sameAvo = (a, b) => (a || "").trim() === (b || "").trim();
  const _allowDiffAvo = () => amendMode && amendType === "Substitution de procureur";
  const btnAjout = () => {
    const sel = Array.isArray(dispoSel) ? dispoSel : [];
    if (sel.length === 0) return;
    const picked = sel.map(i => dispoRows[i]).filter(Boolean);
    const ref = selecRows[0]?.avocat_code ?? picked[0]?.avocat_code;
    let toAdd = picked;
    let skipped = 0;
    if (!_allowDiffAvo()) {
      toAdd = picked.filter(r => _sameAvo(r.avocat_code, ref));
      skipped = picked.length - toAdd.length;
    }
    if (toAdd.length === 0) {
      return showPopup?.(
        `Impossible d'ajouter : l'avocat confié diffère de celui des demandes déjà `
        + `sélectionnées (${ref || "?"}). Une attestation ne regroupe que des demandes `
        + `du même avocat confié (sauf amendement « Substitution de procureur »).`,
        "warning", "Attestation");
    }
    const addedSet = new Set(toAdd);
    setSelecRows([...selecRows, ...toAdd]);
    setDispoRows(dispoRows.filter(r => !addedSet.has(r)));
    setDispoSel([]);
    if (skipped > 0) {
      showPopup?.(
        `${skipped} demande(s) ignorée(s) : avocat confié différent de ${ref || "?"}. `
        + `Une attestation ne regroupe que des demandes du même avocat confié.`,
        "warning", "Attestation");
    }
  };
  const btnAjoutTout  = () => {
    if (dispoRows.length === 0) return;
    if (_allowDiffAvo()) {
      setSelecRows([...selecRows, ...dispoRows]);
      setDispoRows([]);
      setDispoSel([]);
      return;
    }
    const ref = selecRows[0]?.avocat_code ?? dispoRows[0]?.avocat_code;
    const toAdd = dispoRows.filter(r => _sameAvo(r.avocat_code, ref));
    const skipped = dispoRows.length - toAdd.length;
    if (toAdd.length === 0) return;
    setSelecRows([...selecRows, ...toAdd]);
    setDispoRows(dispoRows.filter(r => !_sameAvo(r.avocat_code, ref)));
    setDispoSel([]);
    if (skipped > 0) {
      showPopup?.(
        `${skipped} demande(s) ignorée(s) : avocat confié différent de ${ref || "?"}. `
        + `Une attestation ne regroupe que des demandes du même avocat confié.`,
        "warning", "Attestation");
    }
  };
  const btnEnleve     = () => transferOne(selecRows, dispoRows, selecSelIdx, setSelecRows, setDispoRows, setSelecSelIdx);
  const btnEnleveTout = () => transferAll(selecRows, dispoRows, setSelecRows, setDispoRows, setSelecSelIdx);

  // Quand un DAJ est ajouté aux sélectionnés → pré-remplir Nature + Avocats
  // (parité `SetDocAttest`):
  //   • Avocat mandaté (Confié à) : code = daj.adda712 (avocat_code) → lookup nom/adresse
  //   • Avocat signataire         : code = daj.adda704 → lookup nom/prénom
  //   • Champ Nature : appel backend `GetNatureDossierAtt` (parité stricte)
  useEffect(() => {
    // Bypass quand on ouvre une attestation existante : les champs `adat*`
    // proviennent directement de la table `atattest` et ne doivent pas être
    // écrasés par des valeurs recomputées à partir des DAJ liés.
    if (skipSelecRowsEffectRef.current) {
      skipSelecRowsEffectRef.current = false;
      return;
    }
    if (selecRows.length === 0) {
      setForm(p => ({ ...p, adatnature: "" }));
      return;
    }
    const first = selecRows[0];
    const codeMand = first.avocat_code || "";
    const avoMand = (avocats || []).find(a => (a.code || "").trim() === codeMand.trim()) || {};
    const codeSign = first.avocat_signataire_code || "";
    const avoSign = (avocats || []).find(a => (a.code || "").trim() === codeSign.trim()) || {};

    // Appel backend pour construire le texte Nature (parité legacy `GetNatureDossierAtt`)
    // La langue est récupérée depuis header.langue (= clcllangue du client) —
    // fallback Fr (1) si absent. Parité legacy `MDIParent2.RadLangue1/2/3`.
    const clientLangue = header?.langue !== undefined && header?.langue !== null
      ? parseInt(header.langue, 10)
      : 1;
    axios.post(`${API}/attestations/compute-nature-text`, {
      selec_rows: selecRows.map(r => ({
        nature_code: r.nature_code || "",
        avocat_code: r.avocat_code || "",
        noseqdaj: r.noseqdaj || "",
      })),
      langue: clientLangue,
    }).then(res => {
      const natureText = res?.data?.nature_text || "";
      if (natureText.length > MAX_NATURE_LEN) {
        showPopup?.(
          `Le texte « Nature et objet du dossier » cumulé des demandes sélectionnées `
          + `atteint ${natureText.length} caractères (limite ${MAX_NATURE_LEN}). `
          + `Il devra être raccourci avant l'enregistrement.`,
          "warning", "Attestation");
      }
      setForm(p => ({
        ...p,
        adatnature: natureText,
        adatcodeavomand: codeMand,
        adatavonommand: avoMand.nom || "",
        adatavoprenommand: avoMand.prenom || "",
        adatavoadr: avoMand.rue || "",
        adatavoville: avoMand.ville || "",
        adatavoprov: avoMand.province || "QC",
        adatavocodepostal: avoMand.codepostal || "",
        adatcodeavocsign: codeSign,
        adatavonomsign: avoSign.nom || "",
        adatavoprenomsign: avoSign.prenom || "",
      }));
    }).catch(err => {
      console.error("compute-nature-text failed:", err);
      // Fallback : mise à jour sans le texte nature
      setForm(p => ({
        ...p,
        adatcodeavomand: codeMand,
        adatavonommand: avoMand.nom || "",
        adatavoprenommand: avoMand.prenom || "",
        adatavoadr: avoMand.rue || "",
        adatavoville: avoMand.ville || "",
        adatavoprov: avoMand.province || "QC",
        adatavocodepostal: avoMand.codepostal || "",
        adatcodeavocsign: codeSign,
        adatavonomsign: avoSign.nom || "",
        adatavoprenomsign: avoSign.prenom || "",
      }));
    });
  }, [selecRows, avocats, header]);

  /**
   * Ouvre l'attestation sélectionnée dans la grille Liste (bouton Ouvrir).
   * Délègue à loadAttestation (parité legacy `btnOuvrir_Click`).
   */
  const handleOuvrirAttestExistante = useCallback(() => {
    if (listeSelIdx < 0) return;
    loadAttestation(attestations[listeSelIdx]?.noref, false);
  }, [listeSelIdx, attestations, loadAttestation]);

  const doNouvelle = () => {
    setForm({ ...EMPTY_ATTEST,
      adatreqnom: requerantNom || "", adatreqprenom: requerantPrenom || "",
    });
    setAmendMode(false); setAmendType(""); setAmendSourceNoref("");
    loadEligibles();
    setActiveTab("demande");
  };

  // Amender (parité `btnAmender_Click` → `ReqAmendeAttest`) : charge l'image
  // active dans le formulaire, ouvre les demandes disponibles pour ajout/retrait
  // et active le mode amendement (sélecteur de type requis avant enregistrement).
  const handleAmender = useCallback(async () => {
    const row = attestations[listeSelIdx];
    if (!row?.noref) return;
    if ((row.adatamendable || "").trim() !== "A") {
      return showPopup?.("Cette image n'est pas amendable : seule la dernière image "
        + "active (Amendable = A) peut être amendée.", "warning", "Amender");
    }
    await loadAttestation(row.noref, true);
    try {
      const { data } = await axios.get(`${API}/clients/${noclient}/daj-eligibles-attestation`);
      setDispoRows(data || []);
    } catch { setDispoRows([]); }
    setAmendSourceNoref(row.noref);
    setAmendType("");
    setAmendMode(true);
  }, [attestations, listeSelIdx, loadAttestation, noclient, showPopup]);

  // Suppression (admin) — SANS mot de passe : le rôle admin est le seul contrôle
  // d'accès. Conserve la confirmation du Volet contributif (409) côté backend.
  const handleSupprimer = async () => {
    const noref = attestations[listeSelIdx]?.noref;
    if (!noref) return;
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'attestation No. : ${noref} ?`)) return;
    const cleanupAfterDelete = () => {
      setForm({ ...EMPTY_ATTEST });
      setSelecRows([]); setListeSelIdx(-1); setSelecSelIdx(-1); setDispoSel([]);
      setActiveTab("liste"); loadEligibles();
    };
    const doDelete = (confirmVolet) => axios.delete(
      `${API}/attestations/${encodeURIComponent(noref)}${confirmVolet ? "?confirm_volet=true" : ""}`);
    try {
      const res = await doDelete(false);
      showPopup?.(res.data.message, "success", "Suppression");
      cleanupAfterDelete(); loadAttestations();
      if (onAttestationCreated) onAttestationCreated("", []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "VOLET_CONFIRMATION_REQUIRED") {
        if (window.confirm(detail.message)) {
          try {
            const res2 = await doDelete(true);
            showPopup?.(res2.data.message, "success", "Suppression");
            cleanupAfterDelete(); loadAttestations();
            if (onAttestationCreated) onAttestationCreated("", []);
          } catch (e2) {
            showPopup?.(`Erreur : ${e2.response?.data?.detail || e2.message}`, "error", "Suppression");
          }
        }
      } else {
        showPopup?.(typeof detail === "string" ? detail : (detail?.message || e.message), "error", "Suppression");
      }
    }
  };

  const handleSave = async () => {
    if (selecRows.length === 0)
      return showPopup?.("Sélectionnez au moins un dossier via les boutons de transfert.", "warning", "Attestation");
    if (amendMode && !amendType)
      return showPopup?.("Sélectionnez le type d'amendement avant d'enregistrer.", "warning", "Amender");
    // Validation de la limite de caractères du champ Nature et objet du dossier.
    if ((form.adatnature || "").length > MAX_NATURE_LEN) {
      return showPopup?.(
        `Le champ « Nature et objet du dossier » contient ${(form.adatnature || "").length} `
        + `caractères, au-delà de la limite de ${MAX_NATURE_LEN}. Réduisez le texte `
        + `(ou retirez des demandes) avant d'enregistrer.`,
        "warning", "Attestation");
    }
    // Validation Date émise (parité contrainte legacy)
    const dateErr = validateDateEmise(form.adatdateemis, dateDemandeMin);
    if (dateErr) {
      setDateEmiseError(dateErr);
      return showPopup?.(dateErr, "error", "Date d'émission invalide");
    }
    setSaving(true);
    try {
      const dateFin = form.adatdateacceptfin_date
        ? `|${form.adatdateacceptfin_date}`
        : "Jusqu'à terme|";
      const payload = {
        noclient,
        noseqdaj_list: selecRows.map(r => r.noseqdaj),
        ...form,
        adatdateacceptfin: dateFin,
        adatreqadr: header?.adresse || "",
        adatreqville: header?.ville || "",
        adatreqprov: header?.province || "QC",
        adatreqcodepostal: header?.code_postal || "",
        adatreqdatenais: header?.date_naissance || "",
      };
      const url = amendMode
        ? `${API}/attestations/${encodeURIComponent(amendSourceNoref)}/amend`
        : `${API}/attestations`;
      const finalPayload = amendMode ? { ...payload, type_amendement: amendType } : payload;
      const wasAmend = amendMode;
      const { data } = await axios.post(url, finalPayload);
      if (wasAmend) {
        showPopup?.(`Attestation amendée : nouvelle image ${data.noref} créée (« ${data.amende} »).\n`
          + `Image précédente ${data.ancienne_image_verrouillee} verrouillée. Type : ${data.adattype}.`,
          "success", "Amendement");
      } else {
        showPopup?.(`Attestation ${data.noref} créée avec succès.\n${data.nb_daj} DAJ liés.`, "success", "Attestation");
      }
      // Propager le n° de référence généré au formulaire principal si le DAJ
      // courant est parmi les DAJ liés à cette attestation (parité legacy :
      // `frmDemande.txtNoRef.Text = adda510.noref` après retour de frmAttest).
      if (onAttestationCreated && data.noref) {
        onAttestationCreated(data.noref, finalPayload.noseqdaj_list || []);
      }
      loadAttestations(); loadEligibles();
      setAmendMode(false); setAmendType(""); setAmendSourceNoref("");
      if (wasAmend) {
        // Afficher immédiatement la NOUVELLE image dans l'onglet Attestation
        // (sinon l'onglet reste vide alors que la liste montre le nouveau numéro).
        await loadAttestation(data.noref, true);
      } else {
        setForm({ ...EMPTY_ATTEST });
        setActiveTab("liste");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.blocages) {
        const msg = detail.blocages.map(b => `${b.noseqdaj} :\n  - ${b.raisons.join("\n  - ")}`).join("\n\n");
        showPopup?.(`${detail.message}\n\n${msg}`, "error", "Attestation impossible");
      } else {
        showPopup?.(`Erreur : ${detail || e.message}`, "error", "Attestation");
      }
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  const contrMaxRaw = (header?.clclcontrmax || "0").trim();
  const contrMaxNum = parseFloat(contrMaxRaw.replace(/[^\d.-]/g, "")) || 0;
  const isExempt = contrMaxRaw.startsWith("X");
  // Contribution propre à l'attestation ouverte (fallback quand l'en-tête client
  // n'a pas de contribution, ex. ouverture d'une attestation existante).
  const attContribNum = parseFloat(String(form.adatmntcontrib || "0").replace(/[^\d.-]/g, "")) || 0;
  const hasEntente = Number(form.adatentente) === 1;
  // Bouton Entente actif si : une entente existe déjà (consultation/modif), OU
  // contribution > 0 (dossier courant OU attestation ouverte) et non exempté.
  const enteneEnabled = hasEntente || (!isExempt && (contrMaxNum > 0 || attContribNum > 0));
  const clientTitle = [requerantPrenom, requerantNom].filter(Boolean).join(" ");
  const attestTitle = selecRows.length > 0
    ? `(${selecRows[0].decision === "Att.cond." ? "Attestation conditionnelle" : "Attestation régulière"})`
    : "";

  // === Rendu grille (dispo/sélec avec entêtes teal) ===
  const renderGrid = (rows, cols, selIdx, setSel, testIdPrefix, height = 150, multi = false) => {
    const isSel = (idx) => multi
      ? (Array.isArray(selIdx) && selIdx.includes(idx))
      : (idx === selIdx);
    const onRowClick = (idx, e) => {
      if (!multi) { setSel(idx); return; }
      const cur = Array.isArray(selIdx) ? selIdx : [];
      if (e.ctrlKey || e.metaKey) {
        setSel(cur.includes(idx) ? cur.filter(i => i !== idx) : [...cur, idx]);
      } else if (e.shiftKey && cur.length > 0) {
        const last = cur[cur.length - 1];
        const [a, b] = last <= idx ? [last, idx] : [idx, last];
        const range = [];
        for (let i = a; i <= b; i++) range.push(i);
        setSel(Array.from(new Set([...cur, ...range])));
      } else {
        setSel([idx]);
      }
    };
    return (
    <div style={{ background: "#fff", border: "1px solid #808080", height, overflow: "auto" }}>
      <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
        <thead style={{ position: "sticky", top: 0 }}>
          <tr>{cols.map(c => (
            <th key={c.key} style={{
              background: TEAL_HEADER, color: "#fff", padding: "4px 6px",
              textAlign: "left", fontSize: 12.5, width: c.width, whiteSpace: "pre-line",
              borderRight: "1px solid #002266",
            }}>{c.label}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} style={{ padding: 20, textAlign: "center", color: "#999" }}>&nbsp;</td></tr>
          )}
          {rows.map((r, idx) => (
            <tr key={r.noseqdaj || r.noref || idx} onClick={(e) => onRowClick(idx, e)}
                onDoubleClick={() => {
                  if (testIdPrefix === "liste") {
                    setSel(idx);
                    setTimeout(() => handleOuvrirAttestExistante?.(), 0);
                  }
                }}
                style={{ background: isSel(idx) ? "#316ac5" : "#fff", color: isSel(idx) ? "#fff" : "#000", cursor: "pointer" }}
                data-testid={`${testIdPrefix}-row-${idx}`}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: "3px 6px", borderBottom: "1px solid #eee" }}>{r[c.key] ?? "-"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    );
  };

  const ToolbarButtonInline = null; // (déplacé hors composant)

  return (
    <div className="popup-overlay" data-testid="modal-attestation" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#dbe6f0", border: "1px solid #808080", boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        width: "min(1400px, 98vw)", height: "min(800px, 95vh)", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        {/* Titre style Windows */}
        <div style={{
          background: "linear-gradient(to bottom, #dbe6f0, #b8ccd8)",
          padding: "4px 8px", fontSize: 13, fontWeight: 600, color: "#111",
          borderBottom: "1px solid #808080", display: "flex", justifyContent: "space-between",
        }}>
          <span data-testid="attest-titre">Attestation  {clientTitle}  <span style={{ color: "#666" }}>{attestTitle}</span></span>
          <button onClick={onClose} data-testid="btn-attest-close-x"
                  style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>×</button>
        </div>

        {/* Barre outils — style identique à la barre principale (WinButton bleu marine) */}
        <div className="win-toolbar" style={{ padding: "8px 12px", gap: 8 }}>
          <WinButton IconComponent={FilePlus} onClick={doNouvelle} title="Créer une nouvelle attestation" data-testid="btn-attest-nouvelle">Nouvelle</WinButton>
          <WinButton IconComponent={Save} onClick={handleSave} disabled={saving || selecRows.length === 0} title="Enregistrer l'attestation" data-testid="btn-attest-save">
            {saving ? "..." : "Enregistrer"}
          </WinButton>
          <WinButton
            IconComponent={Printer}
            disabled={!(attestations[listeSelIdx]?.noref || form.noref)}
            onClick={() => {
              const nrf = attestations[listeSelIdx]?.noref || form.noref;
              if (!nrf) {
                showPopup?.("Ouvrez ou sélectionnez d'abord une attestation.",
                  "warning", "Imprimer");
                return;
              }
              // regbur = NoReg (2c) + NoBur (2c) — priorité à la ligne
              // sélectionnée, fallback au header dossier
              const rr = (attestations[listeSelIdx]?.noreg
                          || form.noreg || "").padStart(2, "0").slice(0, 2);
              const bb = (attestations[listeSelIdx]?.nobur
                          || form.nobur || "").padStart(2, "0").slice(0, 2);
              const url = `${API}/attestation/pdf`
                + `?noref=${encodeURIComponent(nrf)}`
                + `&regbur=${encodeURIComponent(rr + bb)}`;
              openReport({ url, title: "Attestation",
                fileName: buildReportName("ATTESTATION", nrf, requerantPrenom, requerantNom) });
              // Suggestion legacy : lettre de substitution de procureur si l'image
              // précédente était confiée à un avocat différent.
              axios.get(`${API}/attestation/${encodeURIComponent(nrf)}/substitution-check`,
                { params: { regbur: rr + bb } })
                .then((r) => { if (r.data?.substitution) setSubstDialog({ noref: nrf, regbur: rr + bb }); })
                .catch(() => {});
            }}
            title="Imprimer l'attestation sélectionnée (PDF, ouvre dans un nouvel onglet)"
            data-testid="btn-attest-print">Imprimer</WinButton>
          <WinButton IconComponent={Handshake} onClick={openEntente} disabled={!enteneEnabled} title="Entente de paiement" data-testid="btn-attest-entente">Entente</WinButton>
          <WinButton IconComponent={FileSignature} onClick={openSubstitution} disabled={!hasSubstitution}
                     title={hasSubstitution
                       ? "Imprimer la lettre de substitution de procureur"
                       : "Aucune substitution de procureur pour cette attestation"}
                     data-testid="btn-attest-substitution">Imprimer substitution</WinButton>
          <div className="win-toolbar-separator" />
          <WinButton IconComponent={DoorClosed} onClick={onClose} title="Fermer" data-testid="btn-attest-close">Fermer</WinButton>
        </div>

        {/* En-tête dossier : Région · Bureau · No. Référence (tous gris, non
            éditables) · Date émise */}
        <div style={{ background: "#dbe6f0", padding: "6px 8px", display: "grid",
                       gridTemplateColumns: "100px 100px 1fr 150px", gap: 12,
                       borderBottom: "1px solid #a0a0a0" }}>
          {[
            { lbl: "Région", val: formData?.region || "90", testId: "hdr-noreg" },
            { lbl: "Bureau", val: formData?.bureau || "01", testId: "hdr-nobur" },
            { lbl: "No. Référence",
              val: form.noref || attestations[listeSelIdx]?.noref || formData?.no_reference || "",
              testId: "hdr-noref" },
          ].map(f => (
            <div key={f.lbl} style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#000" }}>{f.lbl}</label>
              <input value={f.val} readOnly
                     data-testid={f.testId}
                     style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
            </div>
          ))}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#000" }}>Date émise</label>
            <DateInput value={form.adatdateemis}
                       onChange={v => setField("adatdateemis", v)}
                       onBlur={() => setDateEmiseError(validateDateEmise(form.adatdateemis, dateDemandeMin))}
                       testId="hdr-dateemis"
                       style={dateEmiseError ? { border: "2px solid #d13438", background: "#fff2f0" } : {}} />
            {dateEmiseError && (
              <div data-testid="hdr-dateemis-error"
                   style={{ fontSize: 10, color: "#d13438", padding: "2px 4px", fontWeight: 600,
                            background: "#fff2f0", border: "1px solid #d13438", borderTop: "none" }}
                   title={dateEmiseError}>
                ⚠ {dateEmiseError.length > 80 ? dateEmiseError.substring(0, 77) + "…" : dateEmiseError}
              </div>
            )}
          </div>
        </div>

        {/* Onglets — même visuel que les onglets principaux de l'application */}
        <div className="win-tabs" style={{ padding: "6px 8px 0 8px" }}>
          {[
            { id: "demande", lbl: "Demande d'aide juridique" },
            { id: "attestation", lbl: "Attestation" },
            { id: "liste", lbl: "Liste" },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
                    data-testid={`attest-tab-${t.id}`}
                    className={`win-tab${activeTab === t.id ? " active" : ""}`}>{t.lbl}</button>
          ))}
        </div>

        {/* Zone d'onglets à hauteur fixe : scroll interne pour que le modal
            ne change pas de taille en changeant d'onglet. */}
        <div className="attest-tab-scroll" style={{ flex: 1, minHeight: 0, overflow: "auto",
                     display: "flex", flexDirection: "column" }}>
        {/* ========== TAB 1 : DEMANDE D'AIDE JURIDIQUE ========== */}
        {activeTab === "demande" && (
          <div style={{ padding: 8, background: TEAL_BG_LIGHT, flex: 1 }}>
            {/* Bandeau informatif si aucun DAJ éligible (parité legacy MsgBox
                CodeAttest.vb:180-217 "Aucune demande n'est disponible pour émettre
                une nouvelle attestation"). Non-bloquant : les onglets Liste et
                Attestation restent accessibles pour consulter/imprimer/supprimer. */}
            {dispoRows.length === 0 && selecRows.length === 0 && (
              <div data-testid="attest-no-eligible-banner"
                   style={{ background: "#fff4ce", border: "1px solid #ffbb00", padding: "8px 12px",
                            marginBottom: 8, fontSize: 13, color: "#5a4a00" }}>
                <strong>⚠ Aucun dossier n&apos;est disponible pour émettre une nouvelle attestation.</strong><br />
                Prérequis legacy : le dossier doit être <em>Admis</em> ou <em>Att.cond.</em>, complété,
                non annulé, non fermé, sans attestation existante, avec nature et avocat renseignés.<br />
                {attestations.length > 0 && (
                  <>Vous pouvez consulter, imprimer ou supprimer les attestations existantes via l&apos;onglet <strong>Liste</strong>.</>
                )}
              </div>
            )}
            {/* Requérant */}
            <div style={{ marginBottom: 8, border: "1px solid #808080" }}>
              <SectionHeader title="Requérant" />
              <div style={{ background: "#dbe6f0", padding: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 80px 1fr 140px 140px", gap: 6, alignItems: "center", marginBottom: 4 }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Nom :</label>
                  <input value={header?.nom || ""} readOnly data-testid="attest-nom"
                         style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Prénom :</label>
                  <input value={header?.prenom || ""} readOnly data-testid="attest-prenom"
                         style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Date de naissance :</label>
                  <input value={header?.date_naissance || ""} readOnly
                         style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 6, alignItems: "center" }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Adresse:</label>
                  <input value={[header?.adresse, header?.ville, header?.province && `(${header.province})`, header?.code_postal].filter(Boolean).join("  ")}
                         readOnly data-testid="attest-adresse"
                         style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                </div>
              </div>
            </div>

            {/* Grilles + boutons transfert */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 1fr", gap: 6, marginBottom: 8 }}>
              <div style={{ border: "1px solid #808080" }}>
                <SectionHeader title="Demandes disponibles" />
                {renderGrid(dispoRows, GRID_COLS_DISPO, dispoSel, setDispoSel, "dispo", 300, true)}
              </div>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 }}>
                <button onClick={btnAjout} disabled={dispoSel.length === 0} data-testid="btn-ajout"
                        style={{ padding: "4px 10px", fontFamily: "monospace", border: "1px outset #ccc", cursor: "pointer" }}>-&gt;</button>
                <button onClick={btnAjoutTout} disabled={dispoRows.length === 0} data-testid="btn-ajout-tout"
                        style={{ padding: "4px 10px", fontFamily: "monospace", border: "1px outset #ccc", cursor: "pointer" }}>&gt;&gt;</button>
                <button onClick={btnEnleve} disabled={selecSelIdx < 0} data-testid="btn-enleve"
                        style={{ padding: "4px 10px", fontFamily: "monospace", border: "1px outset #ccc", cursor: "pointer" }}>&lt;-</button>
                <button onClick={btnEnleveTout} disabled={selecRows.length === 0} data-testid="btn-enleve-tout"
                        style={{ padding: "4px 10px", fontFamily: "monospace", border: "1px outset #ccc", cursor: "pointer" }}>&lt;&lt;-</button>
              </div>
              <div style={{ border: "1px solid #808080" }}>
                <SectionHeader title="Demandes sélectionnées" />
                {renderGrid(selecRows, GRID_COLS_DISPO, selecSelIdx, setSelecSelIdx, "selec", 300)}
              </div>
            </div>

            {/* Volet contributif */}
            <div style={{ border: "1px solid #808080" }}>
              <SectionHeader title="Volet contributif" />
              <div style={{ background: "#dbe6f0", padding: 12, display: "grid", gridTemplateColumns: "1fr 260px", gap: 20, alignItems: "center" }}>
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "220px 130px", gap: 6, alignItems: "center", marginBottom: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, border: "1px solid #808080", padding: "3px 8px", background: "#dbe6f0" }}>Contribution maximale :</label>
                    <input value={contrMaxRaw ? (isExempt ? contrMaxRaw : `${contrMaxNum.toFixed(2)}$`) : "0.00$"} readOnly data-testid="attest-contmax"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "220px 130px", gap: 6, alignItems: "center" }}>
                    <label style={{ fontSize: 13, fontWeight: 600, border: "1px solid #808080", padding: "3px 8px", background: "#dbe6f0" }}>Coût réel prévisible des services :</label>
                    <input value={`${(form.coureelpre || 0).toFixed ? (form.coureelpre || 0).toFixed(2) : form.coureelpre || 0}$`} readOnly data-testid="attest-coutprev"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                  </div>
                </div>
                <fieldset style={{ border: "1px solid #808080", padding: 8, margin: 0 }}>
                  <legend style={{ fontSize: 13, padding: "0 4px", fontWeight: 600 }}>Entente de paiement</legend>
                  <label style={{ fontSize: 13, marginRight: 12 }}>
                    <input type="radio" name="entente" checked={form.adatentente === 1} disabled={!enteneEnabled}
                           onChange={() => { setField("adatentente", 1); openEntente(); }} data-testid="attest-entente-oui" /> Oui
                  </label>
                  <label style={{ fontSize: 13 }}>
                    <input type="radio" name="entente" checked={form.adatentente === 0} disabled={!enteneEnabled}
                           onChange={handleEntenteNon} data-testid="attest-entente-non" /> Non
                  </label>
                </fieldset>
              </div>
            </div>
          </div>
        )}

        {/* ========== TAB 2 : ATTESTATION ========== */}
        {activeTab === "attestation" && (
          <div style={{ padding: 8, background: TEAL_BG_LIGHT, flex: 1 }}>
            {amendMode && (
              <div data-testid="attest-amend-banner"
                   style={{ marginBottom: 8, border: "1px solid #b8860b", background: "#fff8e1",
                            padding: "8px 10px", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#7a5b00" }}>
                  Amendement de {amendSourceNoref} —
                </span>
                <label style={{ fontSize: 12.5, fontWeight: 600 }}>Type d'amendement :</label>
                <select value={amendType} onChange={e => setAmendType(e.target.value)}
                        data-testid="attest-amend-type"
                        style={{ border: "1px solid #808080", padding: "3px 6px", fontSize: 13, minWidth: 240 }}>
                  <option value="">-- Choisir --</option>
                  {AMEND_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
            {/* Nature et objet du dossier */}
            <div style={{ marginBottom: 8, border: "1px solid #808080" }}>
              <SectionHeader title="Nature et objet du dossier" />
              <textarea value={form.adatnature} onChange={e => setField("adatnature", e.target.value)}
                        rows={5} data-testid="attest-nature" maxLength={MAX_NATURE_LEN}
                        style={{ width: "100%", border: "none", padding: 8, fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
              <div data-testid="attest-nature-count"
                   style={{ textAlign: "right", fontSize: 11, padding: "2px 8px",
                            color: (form.adatnature || "").length > MAX_NATURE_LEN ? "#cc0000" : "#666",
                            background: "#f3f3f3" }}>
                {(form.adatnature || "").length} / {MAX_NATURE_LEN} caractères
              </div>
            </div>

            {/* 2 colonnes : Dossier confié à | Attestation */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              {/* Dossier confié à */}
              <div style={{ border: "1px solid #808080" }}>
                <SectionHeader title="Dossier confié à" />
                <div style={{ background: "#dbe6f0", padding: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 6, alignItems: "center", marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, border: "1px solid #808080", padding: "3px 5px", background: "#dbe6f0" }}>Code :</label>
                    <input value={form.adatcodeavomand || ""} readOnly data-testid="attest-avomand-code"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 6, alignItems: "center", marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, border: "1px solid #808080", padding: "3px 5px", background: "#dbe6f0" }}>Nom :</label>
                    <input value={[form.adatavoprenommand, form.adatavonommand].filter(Boolean).join(" ") || ""} readOnly data-testid="attest-avomand-nom"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 6, alignItems: "start" }}>
                    <label style={{ fontSize: 13, fontWeight: 600, border: "1px solid #808080", padding: "3px 5px", background: "#dbe6f0" }}>Adresse :</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <input value={form.adatavoadr || ""} readOnly
                             style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                      <input value={form.adatavoville || ""} readOnly
                             style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                      <input value={`${form.adatavoprov || ""} ${form.adatavocodepostal || ""}`.trim()} readOnly
                             style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Attestation */}
              <div style={{ border: "1px solid #808080" }}>
                <SectionHeader title="Attestation" />
                <div style={{ background: "#dbe6f0", padding: 8 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 6, alignItems: "center", marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, border: "1px solid #808080", padding: "3px 5px", background: "#dbe6f0" }}>Type d&apos;attestation :</label>
                    <input value={form.amende_label} readOnly data-testid="attest-type"
                           style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 6, alignItems: "center", marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, border: "1px solid #808080", padding: "3px 5px", background: "#dbe6f0" }}>Durée du :</label>
                    <DateInput value={form.adatdateacceptdeb} onChange={v => setField("adatdateacceptdeb", v)} testId="attest-datedeb" />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 6, alignItems: "center", marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, border: "1px solid #808080", padding: "3px 5px", background: "#dbe6f0" }}>Jusqu&apos;au :</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <DateInput value={form.adatdateacceptfin_date} onChange={v => setField("adatdateacceptfin_date", v)} testId="attest-datefin" />
                      <input value={form.adatdateacceptfin_date ? "" : "Jusqu'à terme|"} readOnly
                             style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0", fontStyle: "italic", color: "#666" }} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 6, alignItems: "start" }}>
                    <label style={{ fontSize: 13, fontWeight: 600, border: "1px solid #808080", padding: "3px 5px", background: "#dbe6f0" }}>Code avocat signataire :</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {/* Champ recherche par code OU nom (autocomplete) */}
                      <AvocatAutocomplete liste={avocats} code={form.adatcodeavocsign}
                        nom={[form.adatavonomsign, form.adatavoprenomsign].filter(Boolean).join(", ")}
                        onSelect={(a) => setForm(p => ({ ...p, adatcodeavocsign: a.code, adatavonomsign: a.nom, adatavoprenomsign: a.prenom }))}
                        testId="attest-avosign"
                        placeholder="Rechercher par code (ex: A05022) ou nom…" />
                      {/* Code affiché (parité txtCodeAvo) */}
                      <div style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 4, alignItems: "center" }}>
                        <label style={{ fontSize: 12.5, color: "#555" }}>Code :</label>
                        <input value={form.adatcodeavocsign || ""} readOnly data-testid="attest-avosign-code"
                               style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                      </div>
                      {/* Nom affiché (parité txtNomAvo) */}
                      <div style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 4, alignItems: "center" }}>
                        <label style={{ fontSize: 12.5, color: "#555" }}>Nom :</label>
                        <input value={[form.adatavoprenomsign, form.adatavonomsign].filter(Boolean).join(" ")} readOnly data-testid="attest-avosign-nom"
                               style={{ border: "1px solid #808080", padding: "3px 5px", fontSize: 13, background: "#e0e0e0" }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Boutons Notes / Fondement — parité legacy frmAttest.vb:469-475 :
                copient les valeurs du dossier (onglet Décision) dans « Nature et objet du dossier » */}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="win-btn"
                      onClick={async () => {
                        // Agrège le fondement de droit de TOUTES les demandes de
                        // l'attestation (selecRows). Fallback : dossier courant.
                        try {
                          const rows = (selecRows && selecRows.length > 0)
                            ? selecRows.map(r => ({ noseqdaj: r.noseqdaj }))
                            : (formData?.noseqdaj ? [{ noseqdaj: formData.noseqdaj }] : []);
                          let addition = "";
                          if (rows.length > 0) {
                            const res = await axios.post(
                              `${API}/attestations/compute-fondement-text`,
                              { selec_rows: rows });
                            const txt = (res?.data?.fondement_text || "").trim();
                            if (txt) addition = "   " + txt;
                          }
                          if (!addition) {
                            // Fallback legacy sur le dossier courant si rien d'agrégé
                            const natAcces = (formData?.service?.nature_accessoire || formData?.nature_accessoire || "").trim();
                            const fondDroit = (formData?.service?.fondement_droit || formData?.fondement_droit || "").trim();
                            addition = `${natAcces ? "   " + natAcces : ""}${fondDroit ? "   " + fondDroit : ""}`;
                          }
                          if (!addition.trim()) {
                            showPopup?.("Aucun fondement de droit à ajouter (les champs Nature accessoire et Fondement de droit sont vides pour les demandes sélectionnées).",
                                        "info", "Attestation");
                            return;
                          }
                          const next = (form.adatnature || "") + addition;
                          if (next.length > MAX_NATURE_LEN) {
                            showPopup?.(`L'ajout du fondement de droit dépasserait la limite de ${MAX_NATURE_LEN} caractères (${next.length}). Réduisez le texte ou retirez des demandes.`,
                                        "warning", "Attestation");
                            return;
                          }
                          setField("adatnature", next);
                        } catch (e) {
                          showPopup?.("Erreur lors de la récupération du fondement de droit.", "error", "Attestation");
                        }
                      }}
                      data-testid="btn-fondement"
                      style={{ padding: "6px 14px", fontSize: 13 }}>Ajouter fondement de droit</button>
              <button className="win-btn"
                      onClick={() => {
                        const notes = (formData?.notes || "").trim();
                        if (!notes) {
                          showPopup?.("Aucune note à ajouter (le champ Notes du dossier est vide).",
                                      "info", "Attestation");
                          return;
                        }
                        setField("adatnature", (form.adatnature || "") + "   " + notes);
                      }}
                      data-testid="btn-notes"
                      style={{ padding: "6px 14px", fontSize: 13 }}>Ajouter les notes</button>
            </div>
          </div>
        )}

        {/* ========== TAB 3 : LISTE ========== */}
        {activeTab === "liste" && (
          <div style={{ padding: 8, background: TEAL_BG_LIGHT, flex: 1, display: "grid", gridTemplateColumns: "1fr 130px", gap: 8 }}>
            <div>
              {renderGrid(attestations, GRID_COLS_LISTE, listeSelIdx, setListeSelIdx, "liste", 450)}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button className="win-btn" data-testid="btn-liste-amender"
                      disabled={listeSelIdx < 0 || (attestations[listeSelIdx]?.adatamendable || "").trim() !== "A"}
                      onClick={handleAmender}
                      style={{ padding: "8px", fontSize: 13 }}>Amender</button>
              <button className="win-btn" data-testid="btn-liste-ouvrir"
                      disabled={listeSelIdx < 0}
                      onClick={handleOuvrirAttestExistante}
                      style={{ padding: "8px", fontSize: 13 }}>Ouvrir</button>
              {canSupprimer && (
                <button className="win-btn" data-testid="btn-liste-supprimer"
                        disabled={listeSelIdx < 0}
                        onClick={handleSupprimer}
                        style={{ padding: "8px", fontSize: 13 }}>Supprimer</button>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
      {enteneModal && (
        <EntenteModal
          noref={enteneModal.noref}
          regbur={enteneModal.regbur}
          showPopup={showPopup}
          onClose={() => setEnteneModal(null)}
          onSaved={() => setField("adatentente", 1)}
        />
      )}
      {substDialog && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                      display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2200 }}
             data-testid="subst-dialog"
             onClick={(e) => e.stopPropagation()}>
          <div style={{ background: "#f0f0f0", border: "2px solid #003366", width: 460 }}>
            <div style={{ background: "#003366", color: "#fff", padding: "6px 14px",
                          fontWeight: "bold", fontSize: 13 }}>Substitution de procureur</div>
            <div style={{ padding: "18px 22px" }}>
              <p style={{ margin: "0 0 18px", fontSize: 14 }}>
                Voulez-vous imprimer la lettre de substitution de procureur ?
              </p>
              <p style={{ margin: "0 0 10px", fontSize: 13, color: "#333" }}>Adresser au :</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <button className="win-btn" data-testid="subst-bureau"
                        style={{ padding: "5px 14px" }}
                        onClick={() => {
                          openReport({
                            url: `${API}/attestation/${encodeURIComponent(substDialog.noref)}/substitution-pdf?regbur=${encodeURIComponent(substDialog.regbur)}&dest=bureau`,
                            title: "Substitution de procureur (Bureau)",
                            fileName: buildReportName("SUBSTITUTION", substDialog.noref, requerantPrenom, requerantNom),
                          });
                          setSubstDialog(null);
                        }}>Bureau (BAJ)</button>
                <button className="win-btn" data-testid="subst-centre"
                        style={{ padding: "5px 14px" }}
                        onClick={() => {
                          openReport({
                            url: `${API}/attestation/${encodeURIComponent(substDialog.noref)}/substitution-pdf?regbur=${encodeURIComponent(substDialog.regbur)}&dest=centre`,
                            title: "Substitution de procureur (Centre)",
                            fileName: buildReportName("SUBSTITUTION", substDialog.noref, requerantPrenom, requerantNom),
                          });
                          setSubstDialog(null);
                        }}>Centre communautaire (CCJ)</button>
                <button className="win-btn" data-testid="subst-non"
                        style={{ padding: "5px 14px" }}
                        onClick={() => setSubstDialog(null)}>Non</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttestationModal;
